<template>
  <!-- The long-form legal explainer for the three signature levels. The cards
       in the step itself carry only a one-line summary, so this menu is where
       the full wording (and the knowledge-center link) lives. -->
  <!-- Hover opens it as well as click. The close delay outlives the trip across
       the 4px offset, so the pointer can reach the panel — and the "Learn more"
       link inside it — without the menu closing on the way. -->
  <v-menu
    close-delay="200"
    location="bottom end"
    max-width="320"
    offset="4"
    open-on-click
    open-on-hover
  >
    <template #activator="{ props: activatorProps }">
      <!-- Same icon/size/variant contract DeepDialog gives its own close
           button, so the two sit on one baseline and read as one pair of
           controls rather than two differently-weighted icons. -->
      <v-btn
        v-bind="activatorProps"
        :aria-label="i18n.t('titles.signature_mode_step')"
        data-test-id="signature-mode-info"
        icon="far fa-circle-info"
        size="small"
        variant="plain"
      />
    </template>

    <v-card class="signature-mode-info" flat>
      <div
        v-for="(level, index) in signatureModes"
        :key="level.key"
        class="signature-mode-info__item"
      >
        <p class="signature-mode-info__text">
          <strong>{{ level.title }}</strong> · {{ level.description }}
        </p>
        <v-divider
          v-if="index !== signatureModes.length - 1"
          class="signature-mode-info__divider"
        />
      </div>

      <a
        class="signature-mode-info__link"
        :href="learnMoreLink"
        rel="noopener"
        target="_blank"
      >
        {{ i18n.t('texts.learn_more') }}
      </a>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SignatureMode } from '@/types/enums/SignatureMode';

const i18n = useI18n();

// Always all three levels: this is reference material about what the signature
// levels are, not a picker, so it stays complete even when the organization's
// settings make some of them unselectable.
const signatureModes = computed(() => [
  {
    key: SignatureMode.TIMESTAMP,
    title: i18n.t('titles.basic'),
    description: i18n.t('texts.basic')
  },
  {
    key: SignatureMode.ADVANCED,
    title: i18n.t('titles.advanced'),
    description: i18n.t('texts.advanced')
  },
  {
    key: SignatureMode.QUALIFIED,
    title: i18n.t('titles.qualified'),
    description: i18n.t('texts.qualified')
  }
]);

const learnMoreLink = computed(
  () =>
    `${import.meta.env.VITE_KNOWLEDGE_CENTER_URL}${i18n.t('slugs.signature_levels')}`
);
</script>

<style lang="scss" scoped>
.signature-mode-info {
  padding: 16px;
}

.signature-mode-info__text {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: rgb(var(--v-theme-grey-darken-4));

  strong {
    font-weight: 600;
  }
}

.signature-mode-info__divider {
  margin: 12px 0;
  border-color: rgb(var(--v-theme-grey-lighten-2)) !important;
}

.signature-mode-info__link {
  display: inline-block;
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
